<template>
  <div class="flex flex-col">
    <div class="relative flex flex-1 print:hidden">
      <div class="w-1/4 p-12">
        <div class="absolute top-0 left-0 m-12 xl:m-16">
          <h1 class="flex items-center text-4xl font-bold xl:text-5xl text-primary-500">
            <span v-html="getFormattedFloorName()" />
            <pulse-loader :loading="isLoading" color="#005d74" height="32" width="32" class="inline-flex items-center justify-center ml-2" />
          </h1>
        </div>

        <floor-tower :floor="floor" class="absolute inset-y-0 left-0 z-10 -ml-8 xl:-ml-12 2xl:ml-0" style="height: calc(100vh - 8rem)" />
      </div>

      <div class="relative flex items-center justify-center w-2/4">
        <div class="relative w-full h-full">
          <div class="relative w-full h-full p-8 xl:p-12">
            <div class="relative w-full h-full">
              <transition :name="transitionName">
                <div :key="floor.name" class="relative w-full h-full 2xl:-ml-16">
                  <div
                    class="w-full h-full transform-gpu"
                    :class="{
                      'scale-125': floor.name !== 'Ground',
                      'scale-90 origin-top-right': floor.name === 'Ground',
                    }"
                  >
                    <img :src="`plans/floors/${floor.name}.png`" class="absolute inset-0 object-contain w-full h-full translate-x-8 translate-y-10 2xl:translate-x-0">
                    <inline-svg ref="svg" :src="`plans/floors/${floor.plan}.svg`" class="absolute inset-0 w-full h-full translate-x-8 translate-y-10 2xl:translate-x-0" @loaded="onSvgLoaded" @click="getItemInfo" />
                  </div>
                </div>
              </transition>
            </div>

            <ul v-if="Array.isArray(rooms) && rooms.length > 0" class="absolute inset-x-0 bottom-0 flex justify-center space-x-4 text-xs leading-none align-middle">
              <li class="flex items-center text-green-500">
                <span>Available</span>
                <span class="flex items-center justify-center w-5 h-5 ml-2 text-white bg-green-500 rounded-full">
                  {{ rooms.filter(({ status }) => status === 0).length }}
                </span>
              </li>
              <li class="flex items-center text-red-500">
                <span>Sold</span>
                <span class="flex items-center justify-center w-5 h-5 ml-2 text-white bg-red-500 rounded-full">
                  {{ rooms.filter(({ status }) => [1, 2, 6].includes(status)).length }}
                </span>
              </li>
              <li class="flex items-center text-gray-500">
                <span>On Hold</span>
                <span class="flex items-center justify-center w-5 h-5 ml-2 text-white bg-gray-500 rounded-full">
                  {{ rooms.filter(({ status }) => status === 3).length }}
                </span>
              </li>
            </ul>

            <div class="absolute bottom-0 right-0 p-12 2xl:p-16">
              <img src="@/assets/compass.svg" class="w-auto h-12" alt="">
            </div>
          </div>
        </div>
      </div>

      <div v-if="room" class="flex flex-col justify-center w-1/4 text-white print:hidden">
        <div v-bg-image="'images/shutterstock_110191550_qcut.jpg'" class="relative flex flex-col w-full p-8 bg-cover pointer-events-auto 2xl:p-16">
          <div class="">
            <div class="relative flex flex-col">
              <h1 class="text-2xl font-bold uppercase xl:text-3xl">
                TYPE {{ unit.name }}
              </h1>
              <h2 class="text-base font-bold uppercase xl:text-xl">
                {{ unit.type.name }}
              </h2>
              <h3 class="text-sm xl:text-lg">
                {{ unit.area }} sq.m.
              </h3>
              <span v-if="room.sellingPrice" class="text-xl xl:text-2xl font-compressed">
                <template v-if="room.status === 0">
                  {{ room.sellingPrice.toLocaleString('th-TH') }} บาท
                </template>
                <template v-else>
                &nbsp;
                </template>
              </span>
              <div class="flex space-x-1">
                <button class="flex items-center px-2 py-1 space-x-2 text-sm font-bold text-gray-900 bg-gray-200 rounded-sm shadow-sm" @click="showPanoramaModal = true">
                  VR
                </button>
                <button v-if="isElectron && room.status === 0" class="flex items-center px-2 py-1 space-x-2 text-sm font-bold text-gray-900 bg-gray-200 rounded-sm shadow-sm" :disabled="isPrinting" @click="print">
                  <span>Print</span>
                  <svg v-if="isPrinting" class="w-5 h-5 text-black animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <printer-icon v-else />
                </button>
                <span v-if="room.status === 0" class="px-2 py-1 text-sm font-bold text-white bg-green-500 rounded-sm shadow-sm">
                  Available
                </span>
                <span v-else-if="[1, 2, 6].includes(room.status)" class="px-4 py-1 text-sm font-bold text-white bg-red-500 rounded-sm shadow-sm">
                  Sold
                </span>
                <span v-else-if="room.status === 3" class="px-4 py-1 text-sm font-bold text-white bg-gray-500 rounded-sm shadow-sm">
                  On Hold
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="relative flex items-center justify-center w-full h-full">
          <div class="absolute inset-0 p-8 xl:p-16">
            <unit-image :key="room.unit" :name="room.unit" :rotate="room.rotate" class="flex items-center justify-center" />
          </div>
        </div>
      </div>
      <div v-else class="w-1/4">
        <div v-bg-image="'images/shutterstock_110191550_qcut.jpg'" class="relative flex flex-col w-full p-8 text-white bg-cover pointer-events-auto 2xl:p-16">
          <h1 class="text-2xl font-bold uppercase xl:text-3xl">
            Facilities
          </h1>
          <!-- <h2 class="text-base font-bold uppercase xl:text-xl">
            Facilities Name
          </h2> -->
        </div>
      </div>
    </div>

    <div class="relative flex items-center justify-center h-10 mt-6 text-sm xl:mt-8 xl:text-base print:hidden">
      <div class="relative w-full h-full max-w-screen-md">
        <swiper
          slides-per-view="auto"
          free-mode
          :navigation="{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }"
          class="flex items-center w-full h-full"
          @swiper="onSwiper"
        >
          <swiper-slide v-for="link in getEnabledLinks()" :key="link.id">
            <router-link
              :to="{ name: 'MasterPlansFloors', params: { floorId: link.id } }"
              class="px-2 py-1 transition-all duration-300 xl:px-3"
              active-class="text-white bg-black rounded-full shadow"
            >
              {{ link.name }}
            </router-link>
          </swiper-slide>
        </swiper>
        <div class="-ml-12 swiper-button-prev" />
        <div class="-mr-12 swiper-button-next" />
      </div>
    </div>

    <app-print v-if="room" ref="print" @done="isPrinting = false">
      <div class="text-xl font-bold uppercase">
        {{ room.unitNumber }}
      </div>
      <div class="text-2xl font-bold uppercase">
        TYPE {{ unit.name }}
      </div>
      <div class="text-lg font-bold uppercase">
        {{ unit.type.name }}
      </div>
      <div class="text-base">
        {{ unit.area }} sq.m.
      </div>
      <div v-if="room.sellingPrice" class="text-2xl font-compressed">
        {{ room.sellingPrice.toLocaleString('th-TH') }} บาท
      </div>
      <div v-if="room.pricePerArea" class="text-lg font-compressed">
        {{ room.pricePerArea.toLocaleString('th-TH') }} บาท / ตร.ม.
      </div>
    </app-print>
    <!-- Modal -->
    <app-modal v-if="showPanoramaModal" @close="showPanoramaModal = false">
      <div class="container relative flex items-center justify-center shadow-2xl">
        <div class="aspect-ratio-16/9" />
        <iframe :src="getRoomSkyViewPath()" class="absolute w-full h-full overflow-hidden rounded-sm" frameborder="0" />
      </div>
    </app-modal>
  </div>
</template>

<script>
import anime from 'animejs'
import indicator from 'ordinal/indicator'
import isElectron from 'is-electron'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { PrinterIcon } from '@zhuowenli/vue-feather-icons'
import SwiperCore, { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import AppModal from '@/components/AppModal.vue'
import AppPrint from '@/components/AppPrint.vue'
import UnitImage from '@/components/UnitImage.vue'
import FloorTower from '@/components/FloorTower.vue'

const GetUnitWorker = new Worker('@/workers/GetUnit.js', { type: 'module' })

SwiperCore.use([Navigation])

export default {
  name: 'Floors',
  components: {
    AppModal,
    AppPrint,
    Swiper,
    SwiperSlide,
    PulseLoader,
    PrinterIcon,
    UnitImage,
    FloorTower,
  },
  beforeRouteUpdate (to, from, next) {
    // Get reservation data every times we change floor
    if (to.name === 'MasterPlansFloors' && !to.query.noFetch) {
      this.fetch()
    }

    next()
  },
  props: {
    floorId: {
      type: [Number, String],
      required: false,
      default: 6,
    },
  },
  data () {
    return {
      collections: {
        floors: require('@/assets/data/floors.json'),
        units: require('@/assets/data/units.json'),
        unitTypes: require('@/assets/data/unit_types.json'),
        rooms: require('@/assets/data/rooms.json'),
        // facilities: require('@/assets/data/facilities.json'),
      },

      transitionName: 'page-scale-down',

      roomName: null,
      facilityName: null,

      updatedAt: null,
      isLoading: false,

      statuses: [
        'Available',
        'Sold', // Unknown/Unavailable status
        'Sold',
        'OnHold',
        '', // Unknown/Unavailable status
        '', // Unknown/Unavailable status
        'Sold',
      ],

      showPanoramaModal: false,

      isPrinting: false,
      isElectron: isElectron(),

      timer: null,

      swiper: undefined,
    }
  },
  computed: {
    floor () {
      return this.collections.floors.find(floor => parseInt(this.floorId) === floor.id)
    },
    unit () {
      return this.collections.units.find(unit => unit.name === this.room.unit)
    },
    room () {
      return this.collections.rooms.find(({ name, floor }) => name === this.roomName && floor === this.floor.name)
    },
    rooms () {
      return this.collections.rooms.filter(room => room.floor === this.floor.name)
    },
  },
  watch: {
    '$route' (to, from) {
      this.transitionName = (parseInt(to.params.floorId) > parseInt(from.params.floorId)) ? 'page-scale-down' : 'page-scale-up'
    },
  },
  activated () {
    this.fetch()

    this.setTimer()
  },
  deactivated () {
    clearInterval(this.timer)
  },
  mounted () {
    this.collections.units.forEach(unit => {
      unit.type = this.collections.unitTypes.find(type => type.id === unit.typeId)
    })

    GetUnitWorker.addEventListener('message', ({ data }) => {
      this.collections.rooms = data

      const indicators = this.$el.querySelectorAll('#status-indicators > *')
      const labels = document.querySelectorAll('#status-labels > *')

      indicators.forEach((element, index) => {
        const room = this.rooms.find(room => room.name === element.dataset.name)
        if (room) {
          const status = this.statuses[room.status]
          element.setAttribute('xlink:href', `#StatusIndicator${status}`)
        }
      })

      labels.forEach((element, index) => {
        const room = this.rooms.find(room => room.name === element.dataset.name)
        if (room) {
          const status = this.statuses[room.status]
          element.setAttribute('xlink:href', `#StatusLabel${status}`)
        }
      })

      this.updatedAt = new Date().toISOString()
      this.isLoading = false

      this.setTimer()
    })
  },
  methods: {
    async fetch () {
      if (this.$route.name !== 'MasterPlansFloors') return

      this.isLoading = true

      try {
        GetUnitWorker.postMessage(this.collections.rooms.length)
      } catch {
        this.isLoading = false
      }
    },
    setTimer () {
      clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.fetch()
      }, 15000)
    },
    getEnabledLinks () {
      return [].concat(this.collections.floors.filter(floor => floor.enabled))
    },
    getRoomSkyViewPath () {
      const height = [
        {
          position: 'high',
          floors: ['27', '28', '29', '30', '31', '32', '33', 'Rooftop', 'Rooftop 01', 'Rooftop 02'],
        },
        {
          position: 'mid',
          floors: ['14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26'],
        },
        {
          position: 'low',
          floors: ['06', '07', '08', '09', '10', '11', '12', '12A', '14'],
        },
      ]

      const { position } = height.find(item => item.floors.includes('12'))

      return `panoramas/${position}/${this.room.rotate}/index.html`
    },
    onSwiper (swiper) {
      this.swiper = swiper
    },
    onSvgLoaded (e) {
      const roomEl = e.querySelector('.room')
      const facilityEl = e.querySelector('.facility')

      this.isAnimationFinished = false

      if (roomEl) {
        roomEl.classList.add('is-selected')

        this.facilityName = null
        this.roomName = roomEl.dataset.name
      } else if (facilityEl) {
        facilityEl.classList.add('animate-pulse')

        this.roomName = null
        this.facilityName = facilityEl.dataset.name
      } else {
        this.roomName = null
        this.facilityName = null
      }

      this.animation = anime.timeline({
        delay: anime.stagger(100, { grid: [Math.floor(Math.random() * 3) + 3, Math.floor(Math.random() * 3) + 3], from: 'center' }),
        complete: () => {
          const elements = this.$el.querySelectorAll('.room')
          elements.forEach(element => {
            element.style.opacity = null
          })
        },
      })

      this.animation.add({
        targets: '.room',
        opacity: 0.75,
      })

      this.animation.add({
        targets: '.room',
        opacity: 0,
      })

      this.animation.finished.then(() => {
        this.isAnimationFinished = true
      })
    },
    getItemInfo (e) {
      const roomEl = e.target.closest('.room')
      const facilityEl = e.target.closest('.facility')

      if (!this.isAnimationFinished) {
        return this.cancelAnimation()
      }

      const selectedRoom = this.$el.querySelector('.is-selected')
      const selectedFacility = this.$el.querySelector('.animate-pulse')

      if (roomEl || facilityEl) {
        if (selectedRoom) selectedRoom.classList.remove('is-selected')
        if (selectedFacility) selectedFacility.classList.remove('animate-pulse')
      }

      if (roomEl) {
        roomEl.classList.add('is-selected')

        this.roomName = roomEl.dataset.name
        this.facilityName = null
        this.$router.replace({
          path: this.$route.fullPath,
          query: { unitName: this.room.unit, noFetch: true },
        })
      }

      if (facilityEl) {
        facilityEl.classList.add('animate-pulse')

        this.roomName = null
        this.facilityName = facilityEl.dataset.name
      }
    },
    getFormattedFloorName () {
      const name = this.floor.name

      if (/^(Ground)/i.test(name)) {
        return `${name.toUpperCase()} FLOOR`
      }

      if (/^(\D+)/i.test(name)) {
        return name.toUpperCase()
      }

      if (name === '12A') {
        return `${name}<sup>${indicator(name)}</sup> FLOOR`
      }

      return `${parseInt(name)}<sup>${indicator(name)}</sup> FLOOR`
    },
    cancelAnimation () {
      this.animation.seek(this.animation.duration)
    },
    print () {
      this.isPrinting = true
      this.$refs.print.print()
    },
  },
}
</script>

<style lang="postcss" scoped>
::v-deep(.room) {
  fill: theme("colors.primary.500");
  opacity: 0;
  cursor: pointer;

  &.is-selected {
    @apply opacity-70 !important;
  }
}

::v-deep(.swiper-wrapper) {
  height: unset;
}

::v-deep(.swiper-slide) {
  width: unset;
}

.swiper-button-next,
.swiper-button-prev {
  --swiper-theme-color: theme('colors.escent');
  --swiper-navigation-size: 16px;
}

</style>
